// NeevPetWorld.js — Premium animated virtual pet world
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { showToast, playSound } from '../../utils/appUtils';
import { supabase } from '../../supabase';
import PetChar, { useIdleAnim } from './PetCharacters';
import PetShop, { PetAccessories, getEquipped, getActiveTheme, SHOP_ITEMS } from './PetShop';
import PetVoice from './PetVoice';
import './PetWorldBg.css';

// ─── THEME ───────────────────────────────────
const T = { dark:'#1A1A2E', orange:'#FF6B00', green:'#00C851', yellow:'#FFD600' };

const WORLD_THEMES = {
  default: null, // uses pet's own colors
  ocean:   { sky1:'#0077B6', sky2:'#00B4D8', ground:'#48CAE4', groundDark:'#0096C7',
             tree:'🐠', flower1:'🐙', flower2:'🦀', sun:'🌊', decoration:'🐚' },
  space:   { sky1:'#03045E', sky2:'#090C1F', ground:'#1B1B3A', groundDark:'#0D0D2B',
             tree:'🚀', flower1:'🪐', flower2:'⭐', sun:'🌙', decoration:'🛸' },
  sakura:  { sky1:'#FADADD', sky2:'#FFB7C5', ground:'#F8C8D0', groundDark:'#F4A7B9',
             tree:'🌸', flower1:'🌺', flower2:'🌷', sun:'🌸', decoration:'🦋' },
  desert:  { sky1:'#F4A261', sky2:'#E76F51', ground:'#E9C46A', groundDark:'#F4A261',
             tree:'🌵', flower1:'🦂', flower2:'🐪', sun:'🌞', decoration:'🏜️' },
  candy:   { sky1:'#FF9EBC', sky2:'#FFD6E7', ground:'#FFB3D9', groundDark:'#FF80B5',
             tree:'🍭', flower1:'🍬', flower2:'🍦', sun:'🌈', decoration:'🧁' },
};

// ─── PET DATA — maps all starling IDs ────────────────────────
function makePet(name, tag, body, accent, glow, quotes) {
  return { name, tag, emoji:'✨', body, bodyTop:'#F0EEFF', glow, accent, dark:'#2A1860',
    bg:['#1A0A3E','#0D1B4B'], ground:'#2A1060', groundDark:'#1A0A3E', quotes };
}
const COSMIC_QUOTES = ["The stars shine brighter when you learn! ✨","The cosmos is so proud of you! 🌌","Every answer brings you closer to the stars! ⭐","Your curiosity is your superpower! 🚀","COSMIC LEGEND — the universe bows to you! 👑"];

const PETS = {
  // Core starlings
  neevo:     makePet('Neevo','Cosmic Buddy','#7B5EA7','#FFD700','#7B5EA766', COSMIC_QUOTES),
  luna:      makePet('Luna', 'Creativity Artist','#00BCD4','#0288D1','rgba(0,188,212,0.4)', ["Let's create something magical! 🎨","Your imagination has no limits! ✨","Every idea you have is beautiful! 🌈","I believe in your creativity! 💙","Dream big and make it real! 🚀"]),
  nova_char: makePet('Nova', 'Science Explorer','#9C27B0','#6A1B9A','rgba(156,39,176,0.4)', ["Science is everywhere — let's explore! 🔭","Every question leads to an amazing answer! 🌟","Curiosity is the key! 💜","You're a scientist at heart! 🧪","The universe has so much to show us! 🌌"]),
  spark:     makePet('Spark','Math Genius','#FF9500','#FF6B00','rgba(255,149,0,0.4)', ["Numbers are puzzles waiting to be solved! ⚡","Every maths problem is a challenge! 🔥","You've got a brilliant brain! 🧠","Let's crack this together! 💡","Maths is magic — you're the magician! ✨"]),
  glint:     makePet('Glint','Treasure Hunter','#F59E0B','#D97706','rgba(245,158,11,0.4)', ["Every lesson is a gem waiting to be found! 💎","Shine bright — your brilliance is priceless! ✨","The harder you try, the more you sparkle! 🌟","Hidden treasures reward the curious mind! 🔍","You're pure gold — keep going! 🏆"]),
  orbi:      makePet('Orbi','Planet Explorer','#3B82F6','#1D4ED8','rgba(59,130,246,0.4)', ["The universe is yours to discover! 🪐","Every orbit brings a new adventure! 🚀","Gravity can't hold your imagination! 🌌","Explore, wonder, learn — repeat! 🔭","You're a star in your own galaxy! ⭐"]),
  zuki:      makePet('Zuki','Speed Champion','#EF4444','#B91C1C','rgba(239,68,68,0.4)', ["Fast mind, sharp focus — that's you! ⚡","Push your limits, break every record! 🏁","Speed and smarts — you've got both! 🥷","Nothing can stop a determined learner! 💥","You're unstoppable — let's go! 🔥"]),
  echo:      makePet('Echo','Language Wizard','#10B981','#059669','rgba(16,185,129,0.4)', ["Every word you learn echoes forever! 🌊","Languages open doors to new worlds! 🗣️","Listen, learn, and let your voice shine! 🎵","Communication is your superpower! 💬","Speak it — and the world hears you! 🌍"]),
  terra:     makePet('Terra','Nature Guardian','#84CC16','#4D7C0F','rgba(132,204,22,0.4)', ["The Earth has so much to teach us! 🌿","Nature's secrets are yours to uncover! 🌱","Every living thing has a story — listen! 🦋","You grow just like the tallest tree! 🌳","Protect the planet, protect the future! 🌍"]),
  // Legacy mappings (hidden from selector)
  lumie: { ...makePet('Neevo','Cosmic Buddy','#7B5EA7','#FFD700','#7B5EA766', COSMIC_QUOTES), hidden:true },
  cosmo: { ...makePet('Spark','Math Genius','#FF9500','#FF6B00','rgba(255,149,0,0.4)', COSMIC_QUOTES), hidden:true },
  nova:  { ...makePet('Nova', 'Science Explorer','#9C27B0','#6A1B9A','rgba(156,39,176,0.4)', COSMIC_QUOTES), hidden:true },
};

// Returns character image based on petType and mood
const CHAR_IMGS = {
  neevo:     { idle:'/neevo/Neevo_Smile.png',   happy:'/neevo/Neevo_Happy.png',   excited:'/neevo/Neevo_excited.png',   sad:'/neevo/Neevo_sad.png',   talk:'/neevo/Neevo_talk.png' },
  luna:      { idle:'/luna/luna_smile.png',      happy:'/luna/luna_idle.png',      excited:'/luna/luna_excited.png',      sad:'/luna/luna_sad.png',      talk:'/luna/luna_talk.png' },
  nova_char: { idle:'/nova_char/nova_smile.png', happy:'/nova_char/nova_idle.png', excited:'/nova_char/nova_excited.png', sad:'/nova_char/nova_sad.png', talk:'/nova_char/nova_talk.png' },
  spark:     { idle:'/spark/spark_smile.png',    happy:'/spark/spark_idle.png',    excited:'/spark/spark_excited.png',    sad:'/spark/spark_sad.png',    talk:'/spark/spark_talk.png' },
  echo:      { idle:'/echo/echo_smile.png',       happy:'/echo/echo_happy.png',     excited:'/echo/echo_excited.png',      sad:'/echo/echo_sad.png',      talk:'/echo/echo_talk.png' },
  terra:     { idle:'/terra/terra_smile.png',     happy:'/terra/terra_happy.png',   excited:'/terra/terra_excited.png',    sad:'/terra/terra_sad.png',    talk:'/terra/terra_talk.png' },
  zuki:      { idle:'/zuki/zuki_smile.png',       happy:'/zuki/zuki_happy.png',     excited:'/zuki/zuki_excited.png',      sad:'/zuki/zuki_sad.png',      talk:'/zuki/zuki_talk.png' },
  orbi:      { idle:'/orbi/orbi_smile.png',       happy:'/orbi/orbi_happy.png',     excited:'/orbi/orbi_excited.png',      sad:'/orbi/orbi_sad.png',      talk:'/orbi/orbi_talk.png' },
  glint:     { idle:'/glint/glint_smile.png',     happy:'/glint/glint_happy.png',   excited:'/glint/glint_excited.png',    sad:'/glint/glint_sad.png',    talk:'/glint/glint_talk.png' },
  // Legacy
  lumie: { idle:'/neevo/Neevo_Smile.png', happy:'/neevo/Neevo_Happy.png', excited:'/neevo/Neevo_excited.png', sad:'/neevo/Neevo_sad.png', talk:'/neevo/Neevo_talk.png' },
  cosmo: { idle:'/spark/spark_smile.png', happy:'/spark/spark_idle.png',  excited:'/spark/spark_excited.png',  sad:'/spark/spark_sad.png',  talk:'/spark/spark_talk.png' },
  nova:  { idle:'/nova_char/nova_smile.png', happy:'/nova_char/nova_idle.png', excited:'/nova_char/nova_excited.png', sad:'/nova_char/nova_sad.png', talk:'/nova_char/nova_talk.png' },
};
function getCharImg(petType, mood) {
  const imgs = CHAR_IMGS[petType] || CHAR_IMGS.neevo;
  return imgs[mood] || imgs.idle;
}

const STAGES = [
  { level:1, name:'Egg',    minXP:0,    label:'🥚 Egg',    size:0.65 },
  { level:2, name:'Baby',   minXP:100,  label:'👶 Baby',   size:0.82 },
  { level:3, name:'Kid',    minXP:400,  label:'🧒 Kid',    size:1.0  },
  { level:4, name:'Teen',   minXP:1000, label:'💪 Teen',   size:1.12 },
  { level:5, name:'Legend', minXP:2000, label:'👑 Legend', size:1.25 },
];

const FOODS = [
  { id:'apple',  emoji:'🍎', name:'Apple',   cost:5,  hunger:20, label:'+20 Hunger' },
  { id:'pizza',  emoji:'🍕', name:'Pizza',   cost:15, hunger:50, label:'+50 Hunger' },
  { id:'cake',   emoji:'🎂', name:'Cake',    cost:25, hunger:80, label:'+80 Hunger' },
  { id:'star',   emoji:'⭐', name:'Star Fruit',cost:40,hunger:100,label:'FULL!' },
];

function getStage(xp) {
  let s = STAGES[0];
  for (const st of STAGES) { if (xp >= st.minXP) s = st; }
  return s;
}

// ─── FLOATING PARTICLES ──────────────────────
function Burst({ items, active }) {
  if (!active) return null;
  return (
    <div style={{ position:'absolute', inset:0, pointerEvents:'none', zIndex:20 }}>
      {items.map((item, i) => (
        <motion.div key={i} initial={{ opacity:1, x:0, y:0, scale:0.5 }}
          animate={{ opacity:0, x:(Math.random()-0.5)*140, y:-80-Math.random()*60, scale:1.4 }}
          transition={{ duration:0.9, ease:'easeOut', delay:i*0.06 }}
          style={{ position:'absolute', top:'40%', left:'45%', fontSize:item.size||22, userSelect:'none' }}>
          {item.icon}
        </motion.div>
      ))}
    </div>
  );
}

// ─── ENVIRONMENT BACKGROUND ──────────────────
function PetEnvironment({ pet, stage, activeTheme }) {
  const th = WORLD_THEMES[activeTheme] || null;

  // ── Shop theme active — use classic gradient world ──
  if (th) {
    return (
      <div style={{ position:'absolute', inset:0, overflow:'hidden', borderRadius:'0 0 24px 24px' }}>
        <div style={{ position:'absolute', inset:0, background:`linear-gradient(180deg,${th.sky1} 0%,${th.sky2} 65%,${th.ground} 75%,${th.groundDark} 100%)` }}/>
        <motion.div animate={{ y:[0,-6,0] }} transition={{ duration:4, repeat:Infinity }}
          style={{ position:'absolute', top:'8%', right:'12%', fontSize:40, filter:'drop-shadow(0 0 10px #FFD600)' }}>
          {th.sun}
        </motion.div>
        <div style={{ position:'absolute', bottom:'23%', left:'6%', fontSize:48, lineHeight:1 }}>{th.tree}</div>
        <div style={{ position:'absolute', bottom:'22%', right:'8%', fontSize:30 }}>{th.flower1}</div>
        <div style={{ position:'absolute', bottom:'22%', right:'18%', fontSize:22 }}>{th.flower2}</div>
        <div style={{ position:'absolute', bottom:0, left:0, right:0, height:'25%',
          background:`linear-gradient(180deg,${th.ground},${th.groundDark})`,
          borderRadius:'50% 50% 0 0 / 20px 20px 0 0' }}/>
        {stage.level >= 2 && <div style={{ position:'absolute', bottom:'24%', left:'50%', transform:'translateX(-50%)', fontSize:38 }}>🏠</div>}
        {stage.level >= 5 && <div style={{ position:'absolute', top:'5%', left:'8%', fontSize:28 }}>🌈</div>}
      </div>
    );
  }

  // ── Default — Ultra Premium World ──
  return (
    <div style={{ position:'absolute', inset:0, overflow:'hidden', borderRadius:'0 0 24px 24px' }}>
      {/* Sky */}
      <div className="pwbg-sky"/>
      {/* Aurora */}
      <div className="pwbg-aurora"/>
      {/* Aurora curtains */}
      {[
        {dl:'0s',d:'5s',left:'8%',h:'45%'},
        {dl:'1s',d:'7s',left:'18%',h:'55%',bg:'linear-gradient(180deg,rgba(236,72,153,.5),rgba(168,85,247,.3),transparent)'},
        {dl:'2s',d:'6s',left:'35%',h:'40%',bg:'linear-gradient(180deg,rgba(59,130,246,.55),rgba(16,185,129,.3),transparent)'},
        {dl:'.5s',d:'8s',left:'55%',h:'50%'},
        {dl:'3s',d:'5.5s',left:'72%',h:'45%',bg:'linear-gradient(180deg,rgba(245,158,11,.4),rgba(236,72,153,.3),transparent)'},
        {dl:'1.5s',d:'7.5s',left:'88%',h:'35%'},
      ].map((c,i) => (
        <div key={i} className="pwbg-curtain" style={{'--d':c.d,'--dl':c.dl,left:c.left,height:c.h,...(c.bg?{background:c.bg}:{})}}/>
      ))}
      {/* Nebula puffs */}
      <div className="pwbg-nebula" style={{'--d':'8s','--dl':'0s',width:180,height:100,top:-15,left:-40,background:'radial-gradient(circle,rgba(147,51,234,.4),transparent)'}}/>
      <div className="pwbg-nebula" style={{'--d':'10s','--dl':'2s',width:150,height:90,top:5,right:-20,background:'radial-gradient(circle,rgba(59,130,246,.35),transparent)'}}/>
      <div className="pwbg-nebula" style={{'--d':'7s','--dl':'1s',width:120,height:80,top:'25%',left:'25%',background:'radial-gradient(circle,rgba(236,72,153,.25),transparent)'}}/>
      {/* Mist */}
      <div className="pwbg-mist" style={{'--d':'9s','--dl':'0s',width:200,height:50,top:'55%',left:-30,background:'rgba(100,60,200,.22)'}}/>
      <div className="pwbg-mist" style={{'--d':'11s','--dl':'2s',width:160,height:40,top:'60%',right:-20,background:'rgba(60,80,180,.18)'}}/>
      {/* Stars */}
      {[
        [2,2,'3%','6%',2.1,0],[3,3,'6%','16%',3.2,.4],[2,2,'4%','29%',1.8,1.1],
        [4,4,'8%','44%',2.7,.7,'rgba(200,180,255,.9)'],[2,2,'5%','60%',3.5,1.9],
        [3,3,'10%','73%',2.3,.2],[2,2,'3%','86%',1.9,2.2],[2,2,'14%','20%',2.8,1.4],
        [4,4,'12%','52%',3.1,.6,'rgba(180,200,255,.8)'],[2,2,'16%','80%',2.4,2.6],
        [3,3,'18%','10%',1.7,.9],[2,2,'20%','36%',3.3,1.3],[2,2,'17%','66%',2.0,.3],
        [3,3,'22%','90%',2.9,2.0],[2,2,'26%','4%',3.4,.8],[3,3,'28%','43%',2.2,1.7],
        [5,5,'7%','38%',4,1,'rgba(220,200,255,1)'],[4,4,'15%','76%',3.8,2.5],
      ].map(([w,h,top,left,d,dl,shadow],i) => (
        <div key={i} className="pwbg-star" style={{'--d':`${d}s`,'--dl':`${dl}s`,width:w,height:h,top,left,...(shadow?{boxShadow:`0 0 8px 3px ${shadow}`}:{})}}/>
      ))}
      {/* Shooting stars */}
      {[['5s','0s','7%','-20deg'],['7s','2.5s','14%','-14deg'],['9s','5s','20%','-18deg'],['6s','7s','10%','-25deg']].map(([d,dl,top,rot],i)=>(
        <div key={i} className="pwbg-sstar" style={{'--d':d,'--dl':dl,top,transform:`rotate(${rot})`}}/>
      ))}
      {/* Comet */}
      <div className="pwbg-comet">
        <div style={{position:'relative'}}>
          <div style={{position:'absolute',right:10,top:3,width:90,height:3,background:'linear-gradient(90deg,transparent,rgba(255,200,100,.7))',borderRadius:2,filter:'blur(1px)'}}/>
          <div style={{width:10,height:10,borderRadius:'50%',background:'radial-gradient(circle,#fff,#FFD700)',boxShadow:'0 0 12px 4px rgba(255,215,0,.9),0 0 30px 8px rgba(255,150,0,.5)'}}/>
        </div>
      </div>
      {/* Constellations */}
      <svg style={{position:'absolute',top:'3%',left:'1%',width:130,height:90,pointerEvents:'none'}} viewBox="0 0 130 90" fill="none">
        <line x1="8" y1="70" x2="30" y2="52" stroke="rgba(200,180,255,.3)" strokeWidth="1.2"/>
        <line x1="30" y1="52" x2="58" y2="46" stroke="rgba(200,180,255,.3)" strokeWidth="1.2"/>
        <line x1="58" y1="46" x2="85" y2="58" stroke="rgba(200,180,255,.3)" strokeWidth="1.2"/>
        <line x1="85" y1="58" x2="108" y2="40" stroke="rgba(200,180,255,.3)" strokeWidth="1.2"/>
        <line x1="108" y1="40" x2="120" y2="18" stroke="rgba(200,180,255,.3)" strokeWidth="1.2"/>
        {[[8,70],[30,52],[58,46],[85,58],[108,40],[120,18]].map(([cx,cy],i)=>(
          <circle key={i} cx={cx} cy={cy} r={i%2===0?3:2.5} fill="rgba(220,200,255,.82)"/>
        ))}
      </svg>
      <svg style={{position:'absolute',top:'8%',right:'2%',width:90,height:70,pointerEvents:'none',opacity:.6}} viewBox="0 0 90 70" fill="none">
        <line x1="10" y1="55" x2="35" y2="35" stroke="rgba(180,200,255,.3)" strokeWidth="1"/>
        <line x1="35" y1="35" x2="60" y2="45" stroke="rgba(180,200,255,.3)" strokeWidth="1"/>
        <line x1="60" y1="45" x2="80" y2="20" stroke="rgba(180,200,255,.3)" strokeWidth="1"/>
        {[[10,55],[35,35],[60,45],[80,20]].map(([cx,cy],i)=>(
          <circle key={i} cx={cx} cy={cy} r={i%2===0?2:2.5} fill="rgba(200,220,255,.8)"/>
        ))}
      </svg>
      {/* Moon */}
      <div className="pwbg-moon">
        <div className="pwbg-mring" style={{'--d':'3s','--dl':'0s',inset:-12,borderColor:'rgba(255,215,0,.4)'}}/>
        <div className="pwbg-mring" style={{'--d':'4.5s','--dl':'.8s',inset:-24,borderColor:'rgba(255,215,0,.25)',borderWidth:1}}/>
        <div className="pwbg-mring" style={{'--d':'6s','--dl':'1.6s',inset:-40,borderColor:'rgba(255,215,0,.12)',borderWidth:1}}/>
        <div style={{position:'absolute',width:12,height:12,borderRadius:'50%',background:'rgba(200,140,0,.3)',top:'22%',left:'52%'}}/>
        <div style={{position:'absolute',width:8,height:8,borderRadius:'50%',background:'rgba(200,140,0,.25)',top:'58%',left:'18%'}}/>
      </div>
      {/* Magic orbs */}
      <div className="pwbg-orb" style={{'--d':'4s','--dl':'0s',top:'32%',left:'6%',width:18,height:18,background:'radial-gradient(circle,rgba(168,85,247,.9),rgba(100,40,200,.4))',boxShadow:'0 0 12px 4px rgba(168,85,247,.6)'}}/>
      <div className="pwbg-orb" style={{'--d':'5s','--dl':'1s',top:'28%',right:'12%',width:14,height:14,background:'radial-gradient(circle,rgba(59,130,246,.9),rgba(30,80,200,.4))',boxShadow:'0 0 10px 3px rgba(59,130,246,.6)'}}/>
      <div className="pwbg-orb" style={{'--d':'3.5s','--dl':'2s',top:'40%',left:'45%',width:10,height:10,background:'radial-gradient(circle,rgba(236,72,153,.9),rgba(180,30,120,.4))',boxShadow:'0 0 8px 3px rgba(236,72,153,.6)'}}/>
      <div className="pwbg-orb" style={{'--d':'6s','--dl':'.5s',top:'38%',right:'6%',width:12,height:12,background:'radial-gradient(circle,rgba(16,185,129,.9),rgba(0,120,80,.4))',boxShadow:'0 0 10px 3px rgba(16,185,129,.6)'}}/>
      {/* Rainbow bridge */}
      <div style={{position:'absolute',top:'30%',left:-25,width:220,height:95,overflow:'hidden',pointerEvents:'none',opacity:.65,zIndex:3}}>
        {[['rgba(255,80,80,.65)',220],['rgba(255,170,0,.65)',208],['rgba(255,255,60,.65)',196],['rgba(80,220,80,.65)',184],['rgba(60,160,255,.65)',172],['rgba(160,80,255,.65)',160]].map(([color,w],i)=>(
          <div key={i} className="pwbg-rb-arc" style={{width:w,height:w*0.86,borderTopWidth:5,borderColor:color,margin:i>0?'4px auto 0':0,animationDelay:`${i*0.1}s`}}/>
        ))}
      </div>
      {/* LEFT ISLAND — Grand Castle */}
      <motion.div animate={{y:[0,-14,0]}} transition={{duration:5.5,repeat:Infinity,ease:'easeInOut'}} style={{position:'absolute',top:'18%',left:-28,zIndex:4}}>
        <svg width="125" height="115" viewBox="0 0 125 115" fill="none">
          <ellipse cx="62" cy="100" rx="52" ry="15" fill="#2A0E78"/><ellipse cx="62" cy="95" rx="52" ry="13" fill="#361496"/>
          <path d="M10 95 Q38 104 62 104 Q86 104 114 95 L111 110 Q86 120 62 120 Q38 120 13 110Z" fill="#1E0862"/>
          <rect x="18" y="84" width="6" height="28" rx="3" fill="url(#wfLA)" opacity=".9"/>
          <rect x="96" y="80" width="5" height="32" rx="2" fill="url(#wfLA)" opacity=".8"/>
          <rect x="22" y="38" width="78" height="57" rx="3" fill="#3E1885"/>
          <rect x="22" y="60" width="78" height="2" fill="rgba(255,255,255,.07)"/><rect x="22" y="72" width="78" height="2" fill="rgba(255,255,255,.07)"/>
          <rect x="14" y="26" width="22" height="42" rx="2" fill="#4C20A0"/><rect x="88" y="26" width="22" height="42" rx="2" fill="#4C20A0"/>
          <rect x="42" y="14" width="38" height="54" rx="2" fill="#5C2AB2"/>
          {[14,21,28,88,95,102].map(x=><rect key={x} x={x} y="20" width="5" height="8" rx="1" fill="#6E34CC"/>)}
          {[42,49,56,63,70,77].map(x=><rect key={x} x={x} y="8" width="5" height="8" rx="1" fill="#8040E8"/>)}
          <polygon points="61,0 50,14 72,14" fill="#9A48FF"/>
          <polygon points="25,22 17,30 33,30" fill="#8040E8"/><polygon points="99,22 91,30 107,30" fill="#8040E8"/>
          <line x1="61" y1="0" x2="61" y2="-16" stroke="#FFD700" strokeWidth="1.5"/>
          <polygon points="61,-16 73,-11 61,-6" fill="#FF3333"/>
          <path d="M49 95 Q49 79 61 79 Q73 79 73 95Z" fill="rgba(0,0,0,.65)"/>
          <ellipse cx="61" cy="52" rx="9" ry="11" fill="rgba(255,225,80,.9)"/>
          <ellipse cx="61" cy="52" rx="6.5" ry="8" fill="#FFE040"/>
          <ellipse cx="61" cy="52" rx="11" ry="14" fill="rgba(255,220,0,.18)"/>
          <ellipse cx="27" cy="40" rx="4.5" ry="6" fill="rgba(255,200,60,.65)"/>
          <ellipse cx="97" cy="40" rx="4.5" ry="6" fill="rgba(255,200,60,.65)"/>
          <ellipse cx="40" cy="60" rx="3.5" ry="4.5" fill="rgba(255,180,60,.55)"/>
          <ellipse cx="82" cy="60" rx="3.5" ry="4.5" fill="rgba(255,180,60,.55)"/>
          <defs><linearGradient id="wfLA" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="rgba(140,210,255,.95)"/><stop offset="40%" stopColor="rgba(100,180,255,.5)"/><stop offset="100%" stopColor="transparent"/></linearGradient></defs>
        </svg>
      </motion.div>
      {/* RIGHT ISLAND — Observatory */}
      <motion.div animate={{y:[0,-14,0]}} transition={{duration:6.5,repeat:Infinity,ease:'easeInOut',delay:1.2}} style={{position:'absolute',top:'14%',right:-22,zIndex:4}}>
        <svg width="110" height="105" viewBox="0 0 110 105" fill="none">
          <ellipse cx="55" cy="92" rx="44" ry="13" fill="#0E2078"/><ellipse cx="55" cy="87" rx="44" ry="12" fill="#1428A0"/>
          <path d="M11 87 Q34 97 55 97 Q76 97 99 87 L96 102 Q74 112 55 112 Q36 112 14 102Z" fill="#0A1660"/>
          <rect x="18" y="76" width="5" height="26" rx="2" fill="url(#wfRA)" opacity=".85"/>
          <rect x="22" y="58" width="66" height="30" rx="3" fill="#1C30A0"/>
          <ellipse cx="55" cy="42" rx="30" ry="20" fill="#2040B8"/>
          <path d="M25 42 Q55 20 85 42" fill="#2C4CD0"/>
          <path d="M29 39 Q55 25 81 39" stroke="rgba(200,220,255,.25)" strokeWidth="2" fill="none"/>
          <rect x="52" y="24" width="7" height="20" rx="3" fill="rgba(255,235,100,.6)"/>
          <line x1="55" y1="30" x2="88" y2="7" stroke="#FFD700" strokeWidth="3.5" strokeLinecap="round"/>
          <rect x="84" y="2" width="14" height="9" rx="3" fill="#FFD700" transform="rotate(-34,91,6)"/>
          <circle cx="90" cy="5" r="7" fill="rgba(255,240,150,.35)"/>
          <circle cx="90" cy="5" r="4" fill="rgba(255,245,160,.75)"/>
          <circle cx="90" cy="5" r="2" fill="white"/>
          <line x1="90" y1="5" x2="60" y2="25" stroke="rgba(255,240,150,.2)" strokeWidth="4" strokeLinecap="round"/>
          {[[40,36],[55,30],[70,36],[48,32],[64,33]].map(([cx,cy],i)=><circle key={i} cx={cx} cy={cy} r={i===1?2.5:i>2?1.5:2} fill="rgba(200,220,255,.65)"/>)}
          <rect x="32" y="64" width="12" height="14" rx="2" fill="rgba(100,200,255,.55)"/>
          <rect x="66" y="64" width="12" height="14" rx="2" fill="rgba(100,200,255,.55)"/>
          <path d="M48 87 Q48 76 55 76 Q62 76 62 87Z" fill="rgba(0,0,0,.55)"/>
          <defs><linearGradient id="wfRA" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="rgba(160,225,255,.9)"/><stop offset="100%" stopColor="transparent"/></linearGradient></defs>
        </svg>
      </motion.div>
      {/* TOP CRYSTAL ISLAND */}
      <motion.div animate={{y:[0,-12,0]}} transition={{duration:4,repeat:Infinity,ease:'easeInOut',delay:.7}} style={{position:'absolute',top:'6%',left:'50%',transform:'translateX(-50%)',zIndex:4}}>
        <svg width="80" height="65" viewBox="0 0 80 65" fill="none">
          <ellipse cx="40" cy="57" rx="33" ry="9" fill="#1E0870"/>
          <path d="M7 57 Q22 64 40 64 Q58 64 73 57 L71 65 Q56 72 40 72 Q24 72 9 65Z" fill="#140550"/>
          <polygon points="35,8 28,32 42,32" fill="rgba(168,85,247,.75)"/>
          <polygon points="40,2 33,28 47,28" fill="rgba(196,110,255,.88)"/>
          <polygon points="45,10 39,32 51,32" fill="rgba(140,70,230,.7)"/>
          <polygon points="24,18 19,34 29,34" fill="rgba(120,60,220,.6)"/>
          <polygon points="56,14 51,32 61,32" fill="rgba(150,90,240,.65)"/>
          <circle cx="40" cy="2" r="5" fill="rgba(216,150,255,.6)"/>
          <circle cx="40" cy="2" r="2.5" fill="rgba(230,180,255,.95)"/>
          <circle cx="40" cy="2" r="9" fill="rgba(180,100,255,.2)"/>
        </svg>
      </motion.div>
      {/* CLOUD ISLAND mid-right */}
      <motion.div animate={{y:[0,-12,0]}} transition={{duration:7,repeat:Infinity,ease:'easeInOut',delay:2}} style={{position:'absolute',top:'38%',right:'5%',zIndex:3}}>
        <svg width="65" height="48" viewBox="0 0 65 48" fill="none">
          <ellipse cx="32" cy="42" rx="28" ry="7" fill="#1A1068"/>
          <path d="M4 42 Q18 48 32 48 Q46 48 61 42 L59 50 Q46 56 32 56 Q18 56 6 50Z" fill="#12093A"/>
          <ellipse cx="16" cy="25" rx="11" ry="8" fill="rgba(80,160,255,.55)"/>
          <ellipse cx="32" cy="20" rx="14" ry="10" fill="rgba(100,180,255,.6)"/>
          <ellipse cx="48" cy="25" rx="10" ry="8" fill="rgba(80,160,255,.55)"/>
          <rect x="14" y="33" width="4" height="9" rx="1" fill="rgba(60,40,120,.8)"/>
          <rect x="30" y="30" width="4" height="12" rx="1" fill="rgba(60,40,120,.8)"/>
          <rect x="46" y="33" width="4" height="9" rx="1" fill="rgba(60,40,120,.8)"/>
        </svg>
      </motion.div>
      {/* MAGIC PORTAL */}
      <div style={{position:'absolute',top:'42%',right:'4%',zIndex:6}}>
        <div className="pwbg-portal" style={{'--d':'5s',width:52,height:52,background:'conic-gradient(rgba(168,85,247,.9),rgba(59,130,246,.9),rgba(236,72,153,.9),rgba(245,158,11,.8),rgba(168,85,247,.9))'}}>
          <div style={{position:'absolute',inset:7,borderRadius:'50%',background:'radial-gradient(circle,rgba(15,5,50,.98),rgba(80,20,180,.8))'}}/>
        </div>
        <div className="pwbg-portal-r" style={{'--d':'3s',position:'absolute',top:-7,left:-7,width:66,height:66,background:'conic-gradient(rgba(168,85,247,.3),transparent 40%,rgba(59,130,246,.3),transparent 80%)'}}/>
        {['top:-10px;left:50%','top:55px;left:10px','top:20px;left:60px'].map((s,i)=>(
          <div key={i} className="pwbg-sp" style={{'--d':`${1.2+i*0.3}s`,'--dl':`${i*0.5}s`,'--sz':'9px',...Object.fromEntries(s.split(';').map(x=>{const[k,v]=x.split(':');return[k.trim(),v.trim()]})),position:'absolute'}}>✦</div>
        ))}
      </div>
      {/* Dust particles */}
      {[['45%','30%','rgba(168,85,247,.8)','rgba(168,85,247,.9)','3s','0s'],['50%','65%','rgba(59,130,246,.8)','rgba(59,130,246,.9)','4s','.8s'],['55%','20%','rgba(236,72,153,.8)','rgba(236,72,153,.9)','2.5s','1.5s'],['48%','80%','rgba(245,158,11,.8)','rgba(245,158,11,.9)','3.5s','2s'],['60%','50%','rgba(16,185,129,.8)','rgba(16,185,129,.9)','4.5s','.3s'],['53%','38%','rgba(200,150,255,.8)','rgba(200,150,255,.9)','2.8s','2.8s']].map(([top,left,bg,sh,d,dl],i)=>(
        <div key={i} className="pwbg-dust" style={{'--d':d,'--dl':dl,top,left,background:bg,boxShadow:`0 0 4px ${sh}`}}/>
      ))}
      {/* Fireflies */}
      {[['52%','12%','24px','-35px','rgba(180,255,120,.9)','4.5s','0s'],['57%','75%','-18px','-25px','rgba(150,255,200,.9)','5.5s','1s'],['62%','25%','28px','-30px','rgba(200,255,100,.9)','3.8s','2s'],['55%','88%','-22px','-40px','rgba(160,255,180,.9)','6s','.5s'],['65%','48%','20px','-28px','rgba(180,255,160,.9)','5s','1.5s'],['58%','35%','-16px','-32px','rgba(220,255,130,.9)','4s','3s']].map(([top,left,tx,ty,bg,d,dl],i)=>(
        <div key={i} className="pwbg-ff" style={{'--d':d,'--dl':dl,'--tx':tx,'--ty':ty,top,left,background:bg,boxShadow:`0 0 7px 3px ${bg.replace('.9','.6')}`}}/>
      ))}
      {/* Sparkles */}
      {[['36%','5%','16px','2.2s','0s','✨'],['40%','86%','12px','3.1s','.7s','⭐'],['50%','4%','14px','2.7s','1.5s','💫'],['46%','90%','11px','1.9s','.3s','✦'],['33%','46%','16px','3.4s','2s','🌟'],['56%','7%','12px','2.5s','1.1s','✨'],['54%','84%','13px','3s','.5s','💫'],['42%','70%','11px','2.8s','1.8s','✦'],['38%','24%','14px','3.6s','.9s','⭐'],['62%','62%','10px','2s','2.4s','✨'],['32%','82%','13px','4s','.2s','💫']].map(([top,left,sz,d,dl,icon],i)=>(
        <div key={i} className="pwbg-sp" style={{'--d':d,'--dl':dl,'--sz':sz,top,left}}>{icon}</div>
      ))}
      {/* Runes */}
      {[['34%','3%','4s','0s','✦'],['38%','94%','5s','1s','◈'],['44%','48%','3.5s','2s','⬡'],['52%','18%','6s','.5s','◇'],['60%','90%','4.5s','3s','✧']].map(([top,left,d,dl,r],i)=>(
        <div key={i} className="pwbg-rune" style={{'--d':d,'--dl':dl,top,left}}>{r}</div>
      ))}
      {/* GROUND */}
      <div className="pwbg-ground"/>
      <div className="pwbg-gglow"/>
      <div className="pwbg-gglow2"/>
      {/* Crystals */}
      {[
        {b:'27%',l:'5%',d:'3s',dl:'0s',svg:<svg width="26" height="42" viewBox="0 0 26 42"><polygon points="13,0 4,16 22,16" fill="rgba(168,85,247,.85)"/><rect x="7" y="16" width="12" height="24" rx="1" fill="rgba(130,60,220,.6)"/><line x1="13" y1="2" x2="13" y2="16" stroke="rgba(230,200,255,.5)" strokeWidth="1"/></svg>},
        {b:'26%',l:'14%',d:'4s',dl:'.8s',svg:<svg width="17" height="28" viewBox="0 0 17 28"><polygon points="8,0 2,11 15,11" fill="rgba(100,200,255,.8)"/><rect x="4" y="11" width="9" height="16" rx="1" fill="rgba(60,160,230,.55)"/></svg>},
        {b:'27%',l:'20%',d:'2.5s',dl:'1.6s',svg:<svg width="12" height="20" viewBox="0 0 12 20"><polygon points="6,0 1,8 11,8" fill="rgba(255,200,100,.8)"/><rect x="3" y="8" width="6" height="11" rx="1" fill="rgba(220,160,40,.55)"/></svg>},
        {b:'27%',r:'8%',d:'3.5s',dl:'1.4s',svg:<svg width="22" height="36" viewBox="0 0 22 36"><polygon points="11,0 3,14 19,14" fill="rgba(255,120,200,.8)"/><rect x="5" y="14" width="12" height="20" rx="1" fill="rgba(200,70,160,.6)"/></svg>},
        {b:'26%',r:'17%',d:'2.8s',dl:'2s',svg:<svg width="14" height="24" viewBox="0 0 14 24"><polygon points="7,0 2,10 12,10" fill="rgba(80,220,160,.8)"/><rect x="4" y="10" width="6" height="13" rx="1" fill="rgba(40,180,120,.55)"/></svg>},
        {b:'27%',r:'24%',d:'3.2s',dl:'2.5s',svg:<svg width="10" height="18" viewBox="0 0 10 18"><polygon points="5,0 1,7 9,7" fill="rgba(255,220,80,.8)"/><rect x="3" y="7" width="4" height="10" rx="1" fill="rgba(220,180,40,.55)"/></svg>},
      ].map(({b,l,r,d,dl,svg},i)=>(
        <div key={i} className="pwbg-crys" style={{'--d':d,'--dl':dl,position:'absolute',bottom:b,...(l?{left:l}:{right:r})}}>{svg}</div>
      ))}
      {/* Plants */}
      {[['1%',null,'22px','rgba(80,255,120,.7)','2.5s','0s','🌿'],['7%',null,'20px','rgba(255,140,200,.7)','3s','.5s','🌸'],['14%',null,'18px','rgba(80,255,160,.6)','2.2s','1s','🌱'],['21%',null,'16px','rgba(200,255,100,.6)','3.5s','1.5s','🍀'],[null,'2%','22px','rgba(255,200,60,.7)','3.5s','.3s','🌻'],[null,'9%','20px','rgba(255,140,200,.7)','2.8s','.8s','🌺'],[null,'16%','17px','rgba(100,200,255,.6)','3.2s','1.4s','🌷'],[null,'23%','15px','rgba(200,255,150,.6)','2.6s','2s','🌾']].map(([l,r,sz,gl,d,dl,emoji],i)=>(
        <div key={i} className="pwbg-plant" style={{'--d':d,'--dl':dl,'--sz':sz,'--gl':gl,...(l?{left:l}:{right:r})}}>{emoji}</div>
      ))}
      {/* Ground decorations */}
      <motion.div animate={{y:[0,-8,0]}} transition={{duration:3.5,repeat:Infinity,ease:'easeInOut',delay:1.2}} style={{position:'absolute',bottom:'24%',left:'50%',transform:'translateX(-50%)',fontSize:30,filter:'drop-shadow(0 0 12px rgba(255,200,100,.8))',zIndex:8}}>🏰</motion.div>
      <motion.div animate={{y:[0,-6,0]}} transition={{duration:4,repeat:Infinity,ease:'easeInOut',delay:.6}} style={{position:'absolute',bottom:'24%',left:'25%',fontSize:20,filter:'drop-shadow(0 0 8px rgba(168,85,247,.7))',zIndex:7}}>🌙</motion.div>
      <motion.div animate={{y:[0,-6,0],rotate:[0,10,-10,0]}} transition={{duration:3.8,repeat:Infinity,ease:'easeInOut',delay:2}} style={{position:'absolute',bottom:'24%',right:'23%',fontSize:18,filter:'drop-shadow(0 0 8px rgba(255,215,0,.7))',zIndex:7}}>⭐</motion.div>
      {/* Legend effect */}
      {stage.level >= 5 && (
        <motion.div animate={{scale:[1,1.2,1],opacity:[.7,1,.7]}} transition={{duration:2,repeat:Infinity}} style={{position:'absolute',top:'5%',left:'8%',fontSize:28}}>👑</motion.div>
      )}
      {/* Ground ring */}
      <div className="pwbg-gring"/>
    </div>
  );
}

// ─── STATUS BAR ──────────────────────────────
function StatusBar({ label, value, color, icon }) {
  return (
    <div style={{ flex:1 }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
        <span style={{ fontSize:10, fontWeight:900, color:'rgba(255,255,255,0.8)', fontFamily:"'Nunito',sans-serif" }}>{icon} {label}</span>
        <span style={{ fontSize:10, fontWeight:900, color:'white', fontFamily:"'Nunito',sans-serif" }}>{value}%</span>
      </div>
      <div style={{ background:'rgba(255,255,255,0.2)', borderRadius:6, height:8, overflow:'hidden' }}>
        <motion.div animate={{ width:`${value}%` }} transition={{ duration:0.6 }}
          style={{ height:'100%', background:color, borderRadius:6,
            boxShadow:`0 0 6px ${color}88` }}/>
      </div>
    </div>
  );
}

// ─── PET SELECTOR ────────────────────────────
function PetSelector({ onSelect }) {
  const [chosen, setChosen] = useState(null);
  const [petName, setPetName] = useState('');
  const [step, setStep] = useState(1);

  const confirm = () => {
    if (!petName.trim()) { showToast('Give your pet a name! 🥺', 'info'); return; }
    onSelect(chosen, petName.trim());
  };

  if (step === 2) {
    const pet = PETS[chosen];
    return (
      <div style={{ padding:'32px 24px', textAlign:'center', fontFamily:"'Nunito',sans-serif", background:`linear-gradient(135deg,${pet.bg[0]},${pet.bg[1]})`, minHeight:'100vh' }}>
        <div style={{ marginBottom:20 }}>
          <motion.div animate={{ y:[0,-10,0] }} transition={{ duration:2, repeat:Infinity }}>
            <img src={getCharImg(chosen||'neevo','excited')} alt="Character"
              style={{ width:140, height:140, objectFit:'contain', filter:'drop-shadow(0 8px 20px rgba(123,94,167,0.5))' }}/>
          </motion.div>
        </div>
        <h2 style={{ fontSize:24, fontWeight:900, color:T.dark, margin:'0 0 6px' }}>Name your {pet.name}!</h2>
        <p style={{ fontSize:13, color:'#666', fontWeight:700, margin:'0 0 20px' }}>This name stays forever ✨</p>
        <input value={petName} onChange={e=>setPetName(e.target.value)} maxLength={14}
          placeholder="e.g. Lucky, Bolt, Cosmo..." autoFocus
          style={{ width:'100%', padding:'14px 16px', fontSize:20, fontWeight:900, border:`3px solid ${pet.accent}`,
            borderRadius:16, outline:'none', textAlign:'center', fontFamily:"'Nunito',sans-serif", marginBottom:16, color:T.dark, boxSizing:'border-box', background:'white' }}/>
        <motion.button whileTap={{ scale:0.95 }} onClick={confirm}
          style={{ width:'100%', padding:16, background:`linear-gradient(135deg,${pet.body},${pet.accent})`, border:'none',
            borderRadius:16, fontSize:18, fontWeight:900, color:pet.dark, cursor:'pointer', fontFamily:"'Nunito',sans-serif",
            boxShadow:`0 6px 20px ${pet.glow}` }}>
          🥚 Hatch {petName||'...'}!
        </motion.button>
        <button onClick={()=>setStep(1)} style={{ background:'none', border:'none', color:'#888', fontSize:13, fontWeight:700, marginTop:14, cursor:'pointer', fontFamily:"'Nunito',sans-serif" }}>← Change pet</button>
      </div>
    );
  }

  return (
    <div style={{ padding:'28px 20px', fontFamily:"'Nunito',sans-serif", background:'#FFF8F0', minHeight:'100vh' }}>
      <div style={{ textAlign:'center', marginBottom:24 }}>
        <motion.div animate={{ rotate:[0,10,-10,0] }} transition={{ duration:2, repeat:Infinity }}>
          <span style={{ fontSize:64 }}>🥚</span>
        </motion.div>
        <h2 style={{ fontSize:24, fontWeight:900, color:T.dark, margin:'12px 0 6px' }}>Choose Your NeevPet!</h2>
        <p style={{ fontSize:13, color:'#888', fontWeight:700, margin:0 }}>Your companion grows as you learn. Choose wisely! 🌟</p>
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
        {Object.entries(PETS).filter(([,p])=>!p.hidden).map(([key, pet]) => (
          <motion.div key={key} whileTap={{ scale:0.97 }}
            onClick={()=>{ setChosen(key); setStep(2); }}
            style={{ background:chosen===key?`linear-gradient(135deg,${pet.bg[0]},${pet.bg[1]})`:'white',
              border:`2.5px solid ${chosen===key?pet.accent:'#E8E8E8'}`, borderRadius:22, padding:'18px 20px', cursor:'pointer',
              display:'flex', alignItems:'center', gap:16,
              boxShadow:chosen===key?`0 6px 24px ${pet.glow}`:'0 2px 8px rgba(0,0,0,0.06)', transition:'all 0.2s' }}>
            <motion.div animate={{ y:[0,-6,0] }} transition={{ duration:2, repeat:Infinity }}>
              <img src={getCharImg(key, chosen===key?'excited':'happy')} alt={key}
                style={{ width:70, height:70, objectFit:'contain', filter:'drop-shadow(0 4px 8px rgba(123,94,167,0.4))' }}/>
            </motion.div>
            <div style={{ flex:1 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                <span style={{ fontSize:18, fontWeight:900, color:T.dark }}>{pet.emoji} {pet.name}</span>
                <span style={{ background:pet.body+'33', color:pet.accent, borderRadius:10, padding:'2px 8px', fontSize:10, fontWeight:900 }}>{pet.tag}</span>
              </div>
              <p style={{ margin:0, fontSize:13, color:'#666', fontWeight:700, lineHeight:1.5 }}>{pet.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ─── HOME WIDGET ─────────────────────────────
const NEEVO_QUOTES = [
  "The stars shine brighter when you learn! ✨",
  "Every question you answer makes me stronger! 🌟",
  "The cosmos is proud of you today! 🌌",
  "You're becoming a star — I can feel it! ⭐",
  "Learning is our superpower! 🚀",
];

export function NeevPetWidget({ xp, streak, onOpen }) {
  const petName = localStorage.getItem('ns_pet_name') || 'Neevo';
  const petType = localStorage.getItem('ns_pet_type');
  const h = new Date().getHours();
  const neevoImg = streak === 0 ? getCharImg(petType,'sad') : h < 9 ? getCharImg(petType,'idle') : getCharImg(petType,'happy');
  const quote = NEEVO_QUOTES[Math.floor(Math.random() * NEEVO_QUOTES.length)];

  if (!petType) {
    return (
      <motion.div onClick={onOpen} whileTap={{ scale:0.96 }}
        style={{ margin:'12px 16px 0', background:'linear-gradient(135deg,#F0EEFF,#E8E0FF)',
          border:'2px dashed rgba(123,94,167,0.3)', borderRadius:20, padding:'14px 18px',
          cursor:'pointer', display:'flex', alignItems:'center', gap:14 }}>
        <motion.img src={getCharImg(petType,"idle")} alt="Character"
          animate={{ y:[0,-8,0] }} transition={{ duration:2, repeat:Infinity }}
          style={{ width:60, height:60, objectFit:'contain', flexShrink:0 }}/>
        <div>
          <p style={{ margin:'0 0 2px', fontSize:15, fontWeight:900, color:'#1A1A2E', fontFamily:"'Nunito',sans-serif" }}>Choose Your NeevPet! 🌟</p>
          <p style={{ margin:0, fontSize:12, color:'#888', fontWeight:700, fontFamily:"'Nunito',sans-serif" }}>Your learning buddy is waiting — tap to adopt!</p>
        </div>
      </motion.div>
    );
  }

  const stage = getStage(xp);
  return (
    <motion.div onClick={onOpen} whileTap={{ scale:0.96 }}
      style={{ margin:'12px 16px 0',
        background:'linear-gradient(135deg,#F0EEFF,#E8E0FF)',
        border:'1.5px solid rgba(123,94,167,0.25)', borderRadius:20, padding:'14px 16px',
        display:'flex', alignItems:'center', gap:14, cursor:'pointer',
        boxShadow:'0 4px 18px rgba(123,94,167,0.15)' }}>

      {/* Neevo image — no background, transparent */}
      <motion.img
        src={neevoImg}
        alt="Neevo"
        animate={{ y:[0,-8,0] }}
        transition={{ duration:2.2, repeat:Infinity, ease:'easeInOut' }}
        style={{ width:80, height:80, objectFit:'contain', flexShrink:0,
          filter:'drop-shadow(0 6px 12px rgba(123,94,167,0.35))' }}
      />

      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:3 }}>
          <span style={{ fontSize:15, fontWeight:900, color:'#1A1A2E', fontFamily:"'Nunito',sans-serif" }}>{petName}</span>
          <span style={{ background:'rgba(123,94,167,0.15)', color:'#7B5EA7', borderRadius:8,
            padding:'1px 7px', fontSize:9, fontWeight:900, fontFamily:"'Nunito',sans-serif" }}>
            {stage.label}
          </span>
        </div>
        <p style={{ margin:'0 0 8px', fontSize:12, color:'#555', fontWeight:700, fontStyle:'italic',
          fontFamily:"'Nunito',sans-serif", whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
          "{quote}"
        </p>
        {/* XP progress bar */}
        <div style={{ background:'#E0E0E0', borderRadius:6, height:5, overflow:'hidden' }}>
          <motion.div
            animate={{ width:`${Math.min(100,((xp-(stage.minXP||0))/Math.max(1,((STAGES[stage.level]?.minXP||xp+200)-(stage.minXP||0))))*100)}%` }}
            transition={{ duration:1 }}
            style={{ height:'100%', background:'linear-gradient(90deg,#7B5EA7,#FFD700)', borderRadius:6 }}/>
        </div>
      </div>
      <span style={{ fontSize:22, color:'#AAA' }}>›</span>
    </motion.div>
  );
}

// ─── MAIN PET WORLD SCREEN ───────────────────
export default function NeevPetWorld({ onBack, xp, streak, coins, onCoinsEarned, onXPEarned, profile }) {
  const [petType, setPetType] = useState(()=>localStorage.getItem('ns_pet_type'));
  const [petName, setPetName] = useState(()=>localStorage.getItem('ns_pet_name')||'');
  const [hunger, setHunger] = useState(()=>parseInt(localStorage.getItem('ns_pet_hunger')||'80',10));
  const [happiness, setHappiness] = useState(()=>parseInt(localStorage.getItem('ns_pet_happy')||'80',10));
  const [mood, setMood] = useState('neutral');
  const [walking, setWalking] = useState(false);
  const [burst, setBurst] = useState([]);
  const [burstKey, setBurstKey] = useState(0);
  const [tab, setTab] = useState('world');
  const [showFood, setShowFood] = useState(false);
  const [petX, setPetX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [tapped, setTapped] = useState(false);
  const [toyAnim, setToyAnim] = useState(null);
  const [equipped, setEquipped] = useState(getEquipped);
  const [activeTheme, setActiveTheme] = useState(getActiveTheme);
  const [conversationActive, setConversationActive] = useState(false);
  const voiceRef = useRef(null);
  const idleAnim = useIdleAnim();

  const stage = getStage(xp);
  const pet = petType ? PETS[petType] : null;

  // Pet walks autonomously (paused while dragging)
  const isDraggingRef = useRef(false);
  useEffect(() => {
    if (!petType) return;
    const walk = () => {
      if (isDraggingRef.current) return;
      const newX = (Math.random()-0.5)*120;
      setPetX(newX);
      setWalking(true);
      setTimeout(()=>setWalking(false), 1400);
    };
    const id = setInterval(walk, 5000+Math.random()*4000);
    return ()=>clearInterval(id);
  }, [petType]);

  // Mood from stats
  useEffect(()=>{
    const h=new Date().getHours();
    const newMood = happiness<30||hunger<20?'sad':h<9?'sleepy':happiness>80&&hunger>70?'happy':'neutral';
    if(newMood==='happy') playSound('pet_happy');
    else if(newMood==='sad') playSound('pet_sad');
    else if(newMood==='sleepy') playSound('pet_sleep');
    setMood(newMood);
  },[happiness,hunger]);

  // Save stats
  useEffect(()=>{
    localStorage.setItem('ns_pet_hunger', String(hunger));
    localStorage.setItem('ns_pet_happy', String(happiness));
  },[hunger,happiness]);

  const triggerBurst = (items) => {
    setBurst(items); setBurstKey(k=>k+1);
    setTimeout(()=>setBurst([]),1000);
  };

  const handleTap = () => {
    if (voiceRef.current?.isActive()) {
      voiceRef.current.endConversation();
      return;
    }
    // Wake up the pet for a conversation
    playSound('pet_tap');
    setTapped(true); setTimeout(()=>setTapped(false), 700);
    setMood('loved');
    setHappiness(h=>Math.min(100,h+5));
    triggerBurst([{icon:'💖',size:24},{icon:'✨',size:18},{icon:'💖',size:20},{icon:'🌟',size:16}]);
    setTimeout(()=>setMood('neutral'),1500);
    voiceRef.current?.wakeUp();
  };

  const handleFeed = (food) => {
    if ((coins||0) < food.cost) { showToast('Not enough NeevCoins! 🪙','error'); return; }
    onCoinsEarned && onCoinsEarned(-food.cost);
    playSound('pet_eat');
    setHunger(h=>Math.min(100,h+food.hunger));
    setHappiness(h=>Math.min(100,h+10));
    setMood('eating');
    setShowFood(false);
    triggerBurst([{icon:food.emoji,size:28},{icon:'😋',size:22},{icon:'✨',size:16},{icon:food.emoji,size:20}]);
    showToast(`${petName} loved the ${food.name}! 😋`,'success');
    setTimeout(()=>setMood('neutral'),2000);
  };

  const handleSelect = async (type, name) => {
    localStorage.setItem('ns_pet_type', type);
    localStorage.setItem('ns_pet_name', name);
    playSound('pet_evolve');
    setPetType(type); setPetName(name);
    triggerBurst([{icon:'🎉',size:28},{icon:'✨',size:20},{icon:'⭐',size:24},{icon:'🎊',size:22}]);
    showToast(`${name} has hatched! Welcome! 🥚✨`,'success');
    // Save to Supabase so pet persists across devices
    const { data: { user } } = await supabase.auth.getUser();
    if (user?.email) {
      supabase.from('bm_child_profiles')
        .update({ pet_type: type, pet_name: name })
        .eq('parent_email', user.email)
        .then(() => {}).catch(() => {});
    }
  };

  if (!petType) return <PetSelector onSelect={handleSelect}/>;

  const nextStage = STAGES[stage.level];
  const progressPct = nextStage ? Math.min(100,((xp-stage.minXP)/(nextStage.minXP-stage.minXP))*100) : 100;

  return (
    <div style={{ minHeight:'100vh', fontFamily:"'Nunito',sans-serif", background:'linear-gradient(180deg,#0A0520 0%,#1A0A4E 50%,#0D1B4B 100%)', paddingBottom:80, position:'relative' }}>
      {/* Cosmic background stars */}
      {[...Array(15)].map((_,i)=>(
        <motion.div key={i}
          animate={{opacity:[0.2,1,0.2]}}
          transition={{duration:2+Math.random()*2,repeat:Infinity,delay:Math.random()*3}}
          style={{position:'fixed',width:2,height:2,borderRadius:'50%',background:'white',
            top:`${Math.random()*50}%`,left:`${Math.random()*100}%`,pointerEvents:'none',zIndex:0}}/>
      ))}

      {/* Header */}
      <div style={{ background:'linear-gradient(180deg,rgba(123,94,167,0.4) 0%,transparent 100%)', padding:'14px 16px 16px', position:'relative', zIndex:1 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
          <div>
            <button onClick={onBack} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.6)',
              fontSize:14, fontWeight:800, cursor:'pointer', padding:0, fontFamily:"'Nunito',sans-serif", display:'block', marginBottom:4 }}>
              ← Home
            </button>
            <h2 style={{ margin:0, fontSize:22, fontWeight:900, color:'white' }}>{petName}</h2>
            <p style={{ margin:0, fontSize:11, color:'rgba(255,255,255,0.6)', fontWeight:700 }}>{pet.tag} · {stage.label}</p>
          </div>
          <div style={{ textAlign:'right' }}>
            <p style={{ margin:'0 0 2px', fontSize:20, fontWeight:900, color:'#FFD700' }}>{xp} XP ⭐</p>
            <p style={{ margin:0, fontSize:11, color:'rgba(255,255,255,0.6)', fontWeight:700 }}>🪙 {coins||0} · 🔥{streak}d</p>
          </div>
        </div>
        {/* Status bars */}
        <div style={{ display:'flex', gap:12 }}>
          <StatusBar label="Hunger" value={hunger} color="#FF9500" icon="🍎"/>
          <StatusBar label="Happy" value={happiness} color="#FF80AB" icon="💖"/>
          <StatusBar label="XP" value={progressPct} color="#FFD700" icon="⭐"/>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', borderBottom:'1px solid rgba(255,255,255,0.1)', background:'rgba(255,255,255,0.05)', position:'relative', zIndex:1 }}>
        {[['world','🌍 World'],['shop','🛒 Shop'],['stats','📊 Stats'],['evolve','🥚 Evolve']].map(([id,label])=>(
          <button key={id} onClick={()=>{setTab(id);if(id==='world'){setEquipped(getEquipped());setActiveTheme(getActiveTheme());}}}
            style={{ flex:1, padding:'11px 4px', border:'none', cursor:'pointer', background:'none', fontFamily:"'Nunito',sans-serif",
              fontSize:12, fontWeight:900, color:tab===id?'#FFD700':'rgba(255,255,255,0.4)',
              borderBottom:`3px solid ${tab===id?'#FFD700':'transparent'}`, transition:'all 0.2s' }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── WORLD TAB ── */}
      {tab==='world'&&(
        <>
          {/* Pet environment */}
          <div style={{ position:'relative', height:320, overflow:'hidden' }}>
            <PetEnvironment pet={pet} stage={stage} activeTheme={activeTheme}/>
            {/* Pet character — walks around & draggable */}
            <motion.div
              drag
              dragConstraints={{ left:-120, right:120, top:-60, bottom:60 }}
              dragElastic={0.15}
              dragMomentum={false}
              animate={isDragging ? {} : { x:petX }}
              transition={{ duration:1.3, ease:'easeInOut' }}
              onDragStart={()=>{ setIsDragging(true); isDraggingRef.current=true; }}
              onDragEnd={()=>{ setIsDragging(false); isDraggingRef.current=false; }}
              onClick={handleTap}
              whileDrag={{ scale:1.12, rotate:[-3,3,-3], transition:{ rotate:{ repeat:Infinity, duration:0.3 } } }}
              whileTap={{ scale:0.88 }}
              style={{ position:'absolute', bottom:'23%', left:'50%', transform:'translateX(-50%)',
                cursor:'grab', zIndex:10, userSelect:'none', touchAction:'none' }}>
              {/* Glow ring when conversation is active */}
              {conversationActive && (
                <motion.div animate={{ scale:[1,1.18,1], opacity:[0.6,0.2,0.6] }}
                  transition={{ repeat:Infinity, duration:1.4 }}
                  style={{ position:'absolute', inset:-14, borderRadius:'50%',
                    background:`radial-gradient(circle,${pet.glow} 0%,transparent 70%)`,
                    pointerEvents:'none' }}/>
              )}
              <motion.div animate={isDragging ? { y:[-4,4,-4], transition:{ repeat:Infinity, duration:0.25 } } : { y:[0,-12,0] }}
                transition={{ duration:2.3, repeat:Infinity, ease:'easeInOut' }}>
                <img
                  src={
                    mood==='sad'   ? getCharImg(petType,'sad')     :
                    mood==='loved' ? getCharImg(petType,'excited')  :
                    mood==='happy' ? getCharImg(petType,'happy')    :
                    tapped         ? getCharImg(petType,'talk')     :
                    getCharImg(petType,'idle')
                  }
                  alt="Neevo"
                  style={{ width:210, height:210, objectFit:'contain',
                    filter:'drop-shadow(0 16px 32px rgba(255,215,0,0.4)) drop-shadow(0 0 40px rgba(123,94,167,0.6))' }}
                />
              </motion.div>
              <Burst key={burstKey} items={burst} active={burst.length>0}/>
            </motion.div>
            {/* Tap hint */}
            <div style={{ position:'absolute', bottom:'8%', left:'50%', transform:'translateX(-50%)',
              background:'rgba(255,255,255,0.12)', borderRadius:12, padding:'5px 16px',
              fontSize:11, fontWeight:800, color:'rgba(255,255,255,0.8)', whiteSpace:'nowrap',
              backdropFilter:'blur(8px)', border:'1px solid rgba(255,255,255,0.15)' }}>
              {conversationActive ? `Tap ${petName} to end chat 👋` : `Tap ${petName} to chat! 🎤`}
            </div>
          </div>

          {/* Speech bubble — cosmic style */}
          <div style={{ margin:'14px 16px 0', background:'rgba(255,255,255,0.08)',
            borderRadius:18, padding:'14px 18px',
            boxShadow:'0 4px 24px rgba(123,94,167,0.3)',
            border:'1px solid rgba(255,255,255,0.12)',
            backdropFilter:'blur(12px)', position:'relative' }}>
            <AnimatePresence mode="wait">
              <motion.p key={mood} initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}
                style={{ margin:0, fontSize:15, fontWeight:800, color:'white', textAlign:'center' }}>
                {mood==='happy'&&`Woohoo! You're amazing! ✨`}
                {mood==='loved'&&`Heehee! You're the best! 🥰`}
                {mood==='eating'&&`*chomp chomp* So yummy! 😋`}
                {mood==='sad'&&`I miss you... come study with me! 🥺`}
                {mood==='sleepy'&&`*yaaawn* Good morning! Ready? ☀️`}
                {mood==='neutral'&&pet.quotes[Math.min(stage.level-1,pet.quotes.length-1)]}
              </motion.p>
            </AnimatePresence>
          </div>

          {/* Voice buddy — rendered as fixed overlay, triggered by pet tap */}
          <PetVoice
            ref={voiceRef}
            petType={petType} petName={petName} pet={pet}
            childName={profile?.child_name} xp={xp} streak={streak}
            parentEmail={profile?.parent_email}
            onHappinessChange={v=>setHappiness(h=>Math.min(100,h+v))}
            onConversationChange={setConversationActive}
          />

          {/* Action buttons */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, padding:'12px 16px 0' }}>
            <motion.button whileTap={{ scale:0.95 }} onClick={()=>setShowFood(v=>!v)}
              style={{ background:`linear-gradient(135deg,#FF9500,#FFD600)`, border:'none', borderRadius:16,
                padding:'14px 10px', cursor:'pointer', fontFamily:"'Nunito',sans-serif",
                boxShadow:'0 4px 16px rgba(255,150,0,0.35)' }}>
              <div style={{ fontSize:24 }}>🍎</div>
              <p style={{ margin:'4px 0 0', fontSize:12, fontWeight:900, color:'white' }}>Feed</p>
            </motion.button>
            <motion.button whileTap={{ scale:0.95 }} onClick={()=>{playSound('pet_play');handleTap();}}
              style={{ background:`linear-gradient(135deg,${pet.body},${pet.accent})`, border:'none', borderRadius:16,
                padding:'14px 10px', cursor:'pointer', fontFamily:"'Nunito',sans-serif",
                boxShadow:`0 4px 16px ${pet.glow}` }}>
              <div style={{ fontSize:24 }}>🎮</div>
              <p style={{ margin:'4px 0 0', fontSize:12, fontWeight:900, color:pet.dark }}>Play</p>
            </motion.button>
            <motion.button whileTap={{ scale:0.95 }} onClick={()=>{ playSound('pet_play'); onXPEarned&&onXPEarned(5); setHappiness(h=>Math.min(100,h+8)); triggerBurst([{icon:'📚',size:24},{icon:'⭐',size:20},{icon:'✨',size:16}]); showToast('Study time! +5 XP 📚','success'); }}
              style={{ background:'linear-gradient(135deg,#00695C,#00897B)', border:'none', borderRadius:16,
                padding:'14px 10px', cursor:'pointer', fontFamily:"'Nunito',sans-serif",
                boxShadow:'0 4px 16px rgba(0,105,92,0.35)' }}>
              <div style={{ fontSize:24 }}>📚</div>
              <p style={{ margin:'4px 0 0', fontSize:12, fontWeight:900, color:'white' }}>Study</p>
            </motion.button>
          </div>

          {/* Food picker */}
          <AnimatePresence>
            {showFood&&(
              <motion.div initial={{ opacity:0, y:30 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:20 }}
                style={{ margin:'14px 16px 0', background:'white', borderRadius:20, padding:16, boxShadow:'0 4px 20px rgba(0,0,0,0.1)' }}>
                <p style={{ margin:'0 0 12px', fontSize:14, fontWeight:900, color:T.dark }}>🍎 Choose Food · 🪙 {coins||0} coins</p>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  {FOODS.map(food=>(
                    <motion.button key={food.id} whileTap={{ scale:0.93 }} onClick={()=>handleFeed(food)}
                      style={{ background:(coins||0)>=food.cost?`linear-gradient(135deg,#FFF8E1,#FFF3E0)`:'#F5F5F5',
                        border:`2px solid ${(coins||0)>=food.cost?'#FFD600':'#E0E0E0'}`, borderRadius:14,
                        padding:'12px 8px', cursor:(coins||0)>=food.cost?'pointer':'not-allowed',
                        fontFamily:"'Nunito',sans-serif", opacity:(coins||0)>=food.cost?1:0.5 }}>
                      <div style={{ fontSize:28, marginBottom:4 }}>{food.emoji}</div>
                      <p style={{ margin:0, fontSize:12, fontWeight:900, color:T.dark }}>{food.name}</p>
                      <p style={{ margin:'2px 0 0', fontSize:10, fontWeight:700, color:'#00C851' }}>{food.label}</p>
                      <p style={{ margin:'2px 0 0', fontSize:11, fontWeight:900, color:'#FF9500' }}>🪙 {food.cost}</p>
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}


      {/* ── SHOP TAB ── */}
      {tab==='shop'&&(
        <PetShop
          coins={coins}
          onCoinsEarned={onCoinsEarned}
          onXPEarned={onXPEarned}
          onHungerChange={v=>setHunger(h=>Math.min(100,h+v))}
          onHappinessChange={v=>setHappiness(h=>Math.min(100,h+v))}
          onToy={anim=>{playSound('pet_play');setToyAnim(anim);setTimeout(()=>setToyAnim(null),2000);}}
          onThemeChange={t=>setActiveTheme(t)}
          petName={petName}
        />
      )}

      {/* ── STATS TAB ── */}
      {tab==='stats'&&(
        <div style={{ padding:16 }}>
          <div style={{ background:'white', borderRadius:20, padding:20, boxShadow:'0 2px 12px rgba(0,0,0,0.07)', marginBottom:14 }}>
            <p style={{ margin:'0 0 16px', fontSize:15, fontWeight:900, color:T.dark }}>📊 {petName}'s Journey</p>
            {[
              {label:'Total XP',value:`${xp} XP`,emoji:'⭐',color:'#FF9500'},
              {label:'Day Streak',value:`${streak} days`,emoji:'🔥',color:'#FF2D55'},
              {label:'Hunger',value:`${hunger}%`,emoji:'🍎',color:'#FF9500'},
              {label:'Happiness',value:`${happiness}%`,emoji:'💖',color:'#E91E8C'},
              {label:'Evolution',value:stage.label,emoji:'🥚',color:pet.accent},
            ].map((s,i)=>(
              <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                padding:'12px 0', borderBottom:i<4?'1px solid #F5F5F5':'none' }}>
                <span style={{ fontSize:13, fontWeight:700, color:'#555', fontFamily:"'Nunito',sans-serif" }}>{s.emoji} {s.label}</span>
                <span style={{ fontSize:14, fontWeight:900, color:s.color, fontFamily:"'Nunito',sans-serif" }}>{s.value}</span>
              </div>
            ))}
          </div>
          <div style={{ background:'white', borderRadius:20, padding:20, boxShadow:'0 2px 12px rgba(0,0,0,0.07)' }}>
            <p style={{ margin:'0 0 14px', fontSize:15, fontWeight:900, color:T.dark }}>💬 {petName}'s Voice</p>
            {pet.quotes.map((q,i)=>(
              <div key={i} style={{ display:'flex', gap:10, marginBottom:10 }}>
                <span style={{ background:i<stage.level?pet.body:'#E0E0E0', color:i<stage.level?pet.dark:'#AAA',
                  borderRadius:'50%', width:24, height:24, minWidth:24, display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:10, fontWeight:900, fontFamily:"'Nunito',sans-serif" }}>{i+1}</span>
                <p style={{ margin:0, fontSize:13, fontWeight:700, color:i<stage.level?T.dark:'#CCC', fontStyle:'italic', lineHeight:1.5, fontFamily:"'Nunito',sans-serif" }}>"{q}"</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── EVOLVE TAB ── */}
      {tab==='evolve'&&(
        <div style={{ padding:16 }}>
          <p style={{ fontSize:13, color:'#888', fontWeight:700, margin:'0 0 14px', fontFamily:"'Nunito',sans-serif" }}>
            Earn XP by answering questions to evolve {petName}! 🚀
          </p>
          {STAGES.map((st,i)=>{
            const unlocked=xp>=st.minXP, current=stage.level===st.level;
            return (
              <motion.div key={i} whileHover={{ y:-2 }}
                style={{ background:current?`linear-gradient(135deg,${pet.bg[0]},${pet.bg[1]})`:unlocked?'white':'#FAFAFA',
                  borderRadius:20, padding:'16px', marginBottom:10,
                  border:`2px solid ${current?pet.accent:unlocked?pet.body+'44':'#E8E8E8'}`,
                  display:'flex', alignItems:'center', gap:16, opacity:unlocked?1:0.55,
                  boxShadow:current?`0 6px 20px ${pet.glow}`:'0 1px 6px rgba(0,0,0,0.05)' }}>
                <div style={{ opacity:unlocked?1:0.3 }}>
                  <img src={getCharImg(petType,current?"excited":unlocked?"happy":"idle")}
                    alt="Neevo" style={{ width:60, height:60, objectFit:'contain',
                    filter:unlocked?'drop-shadow(0 4px 8px rgba(123,94,167,0.4))':'grayscale(100%)' }}/>
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                    <span style={{ fontSize:16, fontWeight:900, color:T.dark, fontFamily:"'Nunito',sans-serif" }}>{st.label}</span>
                    {current&&<span style={{ background:pet.body, color:pet.dark, borderRadius:8, padding:'2px 8px', fontSize:9, fontWeight:900, fontFamily:"'Nunito',sans-serif" }}>NOW</span>}
                    {unlocked&&!current&&<span style={{ color:'#00C851', fontSize:14 }}>✅</span>}
                  </div>
                  <p style={{ margin:0, fontSize:12, color:'#888', fontWeight:700, fontFamily:"'Nunito',sans-serif" }}>
                    {st.minXP===0?'Starting form':`Unlocks at ${st.minXP} XP`}
                    {!unlocked&&` · ${st.minXP-xp} XP away`}
                  </p>
                  {current&&nextStage&&(
                    <div style={{ marginTop:8 }}>
                      <div style={{ background:'#E0E0E0', borderRadius:6, height:6, overflow:'hidden' }}>
                        <motion.div initial={{ width:0 }} animate={{ width:`${progressPct}%` }} transition={{ duration:1 }}
                          style={{ height:'100%', background:`linear-gradient(90deg,${pet.body},${pet.accent})`, borderRadius:6 }}/>
                      </div>
                      <p style={{ margin:'4px 0 0', fontSize:10, color:'#AAA', fontWeight:700, fontFamily:"'Nunito',sans-serif" }}>
                        {Math.round(progressPct)}% to {nextStage.label}
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
